<template>
    <div class="mt-8 rounded-2xl border border-app bg-card/70 backdrop-blur-2xl p-6 shadow-lg">
        <!-- Success -->
        <div v-if="step === 'done'" class="text-center py-6">
            <div class="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-500/10 text-brand-500">
                <CheckCircle2 class="h-9 w-9" />
            </div>
            <h4 class="text-xl font-bold text-header mb-2">Registration confirmed</h4>
            <p class="text-body/80 text-sm leading-relaxed mb-6">
                We've sent a confirmation to <span class="text-header font-semibold">{{ form.email }}</span>.
                Your reference is <span class="text-header font-mono font-semibold">{{ reference }}</span>.
            </p>
            <button type="button" @click="emit('reset')"
                class="px-6 py-3 rounded-2xl bg-brand-500 text-on-brand font-bold hover:bg-brand-600 transition-all active:scale-95">
                Book more tickets
            </button>
        </div>

        <!-- Payment -->
        <PaymentPanel v-else-if="step === 'payment'" :order-id="orderId" :amount="form.amount" :currency="currency"
            :contact-phone="form.phone" @paid="emit('paid')" />

        <form v-else class="space-y-6" @submit.prevent="emit('submit')">
            <h3 class="text-header font-bold text-lg">Your order</h3>

            <!-- Summary + optional details per ticket -->
            <div class="rounded-2xl bg-card border border-app divide-y divide-app text-sm space-y-4">
                <div v-for="line in lines" :key="line.key" class="p-4">

                    <div class="flex justify-between gap-3">
                        <span class="text-header font-semibold">{{ line.ticket.title }} × {{ line.quantity }}</span>
                        <span class="text-header font-semibold">{{ formatMoney(line.ticket.price * line.quantity, currency) }}</span>
                    </div>

                    <details class="group/details mt-2">
                        <summary
                            class="inline-flex cursor-pointer select-none list-none items-center gap-1 text-xs font-bold text-brand-500 [&::-webkit-details-marker]:hidden">
                            <ChevronRight class="h-3.5 w-3.5 transition-transform group-open/details:rotate-90" />
                            Ticket holder details <span class="text-body/80 font-medium">(optional)</span>
                        </summary>
                        <div class="mt-3 space-y-4">
                            <TicketHolderCard v-for="(holder, h) in line.holders" :key="h"
                                :holder="holder" :label="line.quantity > 1 ? `Ticket ${h + 1}` : ''"
                                @update:holder="emit('update:holder', line.key, h, $event)" />
                        </div>
                    </details>
                </div>
                <div v-if="form.serviceFee > 0" class="flex justify-between gap-3 p-4">
                    <span class="text-body/80">Service fee</span>
                    <span class="text-header font-semibold">{{ formatMoney(form.serviceFee, currency) }}</span>
                </div>
            </div>

            <!-- Buyer -->
            <div v-if="false" class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label class="block">
                    <span class="text-xs font-bold text-body/80 uppercase tracking-wider">First name</span>
                    <input v-model.trim="form.firstName" type="text" required autocomplete="given-name" :class="fieldClass" />
                </label>
                <label class="block">
                    <span class="text-xs font-bold text-body/80 uppercase tracking-wider">Last name</span>
                    <input v-model.trim="form.lastName" type="text" required autocomplete="family-name" :class="fieldClass" />
                </label>
                <label class="block">
                    <span class="text-xs font-bold text-body/80 uppercase tracking-wider">Email</span>
                    <input v-model.trim="form.email" type="email" required autocomplete="email" :class="fieldClass" />
                </label>
                <label class="block">
                    <span class="text-xs font-bold text-body/80 uppercase tracking-wider">Phone <span class="normal-case font-medium">(optional)</span></span>
                    <input v-model.trim="form.phone" type="tel" autocomplete="tel" :class="fieldClass" />
                </label>
            </div>

            <!-- Total -->
            <div class="flex items-center justify-between border-t border-app pt-4">
                <span class="text-sm font-bold text-header uppercase tracking-wider">Total</span>
                <span class="text-2xl font-black text-header">{{ formatMoney(form.amount, currency) }}</span>
            </div>

            <p v-if="error" class="text-sm text-red-500 font-semibold">{{ error }}</p>

            <button type="submit" :disabled="step === 'processing'"
                class="w-full px-5 py-3.5 rounded-2xl bg-brand-500 text-on-brand font-bold hover:bg-brand-600 transition-all active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2">
                <span v-if="step === 'processing'" class="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                {{ step === 'processing' ? 'Processing…' : (form.amount > 0 ? `Pay ${formatMoney(form.amount, currency)}` : 'Reserve free tickets') }}
            </button>
            <p class="text-center text-[11px] text-body/80">By registering you agree to the event terms and code of conduct.</p>
        </form>
    </div>
</template>

<script setup lang="ts">
import { CheckCircle2, ChevronRight } from 'lucide-vue-next';
import { EventTicketType, Order, TicketHolder } from '~/types';
import { formatMoney } from '../helpers';
import TicketHolderCard from './TicketHolderCard.vue';
import PaymentPanel from './PaymentPanel.vue';

defineProps<{
    lines: { key: string; ticket: EventTicketType; quantity: number; holders: TicketHolder[] }[];
    form: Order;
    currency: string;
    step: 'form' | 'processing' | 'payment' | 'done';
    error: string;
    reference: string;
    orderId: string;
}>();

const emit = defineEmits<{
    (e: 'update:holder', key: string, index: number, value: TicketHolder): void;
    (e: 'submit'): void;
    (e: 'reset'): void;
    (e: 'paid'): void;
}>();

const fieldClass =
    'mt-1.5 w-full rounded-xl border border-app bg-card/70 px-3.5 py-2.5 text-sm text-header outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-all';
</script>

<style scoped>
.bg-card {
    background-color: rgb(var(--core-card-bg));
}

.divide-app> :not([hidden])~ :not([hidden]) {
    border-color: rgb(var(--core-app-border));
}

.border-app {
    border-color: rgb(var(--core-app-border));
}

.text-header {
    color: rgb(var(--core-header-text));
}

.text-muted {
    color: rgb(var(--core-muted-text));
}
</style>
